// src/pages/ProductCatalogPage.tsx
// Product Catalog management page for qmLEO
// Allows admins to view, create, edit, and manage product catalog

import React, { useState, useEffect } from 'react';
import { productCatalogAPI, Product, ProductFilters } from '../services/productCatalogAPI';
import { categoryAPI } from '../services/categoryAPI';
import { purchaseLineItemsAPI, ProductOnOrderSummary } from '../services/purchaseLineItemsAPI';
import { inventoryAPI } from '../services/inventoryAPI';
import { CategoryHierarchy } from '../types/category';
import AddProductModal from '../components/product/AddProductModal';
import EditProductModal from '../components/product/EditProductModal';
import { AlertTriangle, Package, ShoppingCart, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DoubleOrderWarningModal from '../components/purchases/DoubleOrderWarningModal';

const ProductCatalogPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryHierarchy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<string>('all');

  // Stock and order tracking
  const [productStock, setProductStock] = useState<Record<string, { current: number; min: number }>>({});
  const [productsOnOrder, setProductsOnOrder] = useState<Record<string, ProductOnOrderSummary>>({});

  // Selection state for bulk actions
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  // Navigation
  const navigate = useNavigate();

  // Double-order warning modal state
  const [showDoubleOrderWarning, setShowDoubleOrderWarning] = useState(false);
  const [productsWithOpenOrders, setProductsWithOpenOrders] = useState<Array<{
    product: Product;
    orderInfo: ProductOnOrderSummary;
  }>>([]);

  // Load products and categories on mount
  useEffect(() => {
    loadData();
  }, []);

 // Apply filters when products or filter states change
  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, categoryFilter, statusFilter, typeFilter, stockFilter, productStock]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load categories first
      const categoryResponse = await categoryAPI.getAllCategories();
      if (categoryResponse.success && categoryResponse.data) {
        setCategories(categoryResponse.data.categories);
        console.log('📂 Loaded categories:', categoryResponse.data.categories.length);
      }

      // Load products
      const filters: ProductFilters = {};
      
      // Apply status filter at API level
      if (statusFilter === 'active') {
        filters.active = true;
      } else if (statusFilter === 'inactive') {
        filters.active = false;
      }
      
      const fetchedProducts = await productCatalogAPI.listProducts(filters);
      setProducts(fetchedProducts);
      console.log('📦 Loaded products:', fetchedProducts.length);

      // Load inventory stock levels
      const inventoryResponse = await inventoryAPI.getItems();
      if (inventoryResponse.success && inventoryResponse.data) {
        const stockMap: Record<string, { current: number; min: number }> = {};
        
        // Group inventory by productId (if linked) or by name match
        inventoryResponse.data.forEach((item: any) => {
          // For bulk items, aggregate by product
          if (item.productId) {
            if (!stockMap[item.productId]) {
              stockMap[item.productId] = { current: 0, min: item.minStockLevel || 0 };
            }
            stockMap[item.productId].current += item.availableQuantity || 0;
          }
        });
        
        // Also check products with minStockLevel set directly
        fetchedProducts.forEach(product => {
          if (!stockMap[product.productId] && product.minStockLevel) {
            stockMap[product.productId] = { current: 0, min: product.minStockLevel };
          }
        });
        
        setProductStock(stockMap);
        console.log('📊 Loaded stock levels for products:', Object.keys(stockMap).length);
      }

      // Load open line items (on order)
      try {
        const onOrderResponse = await purchaseLineItemsAPI.getOpenLineItemsByProduct();
        const onOrderMap: Record<string, ProductOnOrderSummary> = {};
        onOrderResponse.data.forEach(item => {
          if (item.productId) {
            onOrderMap[item.productId] = item;
          }
        });
        setProductsOnOrder(onOrderMap);
        console.log('🚚 Loaded on-order data for products:', Object.keys(onOrderMap).length);
      } catch (err) {
        console.warn('Could not load on-order data:', err);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];
    
    // Search filter (name, manufacturer, model)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(search) ||
        (p.manufacturer && p.manufacturer.toLowerCase().includes(search)) ||
        (p.model && p.model.toLowerCase().includes(search))
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.categoryId === categoryFilter);
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => p.productType === typeFilter);
    }

    // Stock filter
    if (stockFilter === 'low') {
      filtered = filtered.filter(p => {
        const stock = productStock[p.productId];
        if (!stock) return false;
        return stock.current < stock.min && !productsOnOrder[p.productId];
      });
    } else if (stockFilter === 'on-order') {
      filtered = filtered.filter(p => !!productsOnOrder[p.productId]);
    } else if (stockFilter === 'out') {
      filtered = filtered.filter(p => {
        const stock = productStock[p.productId];
        return stock && stock.current === 0;
      });
    }
    
    setFilteredProducts(filtered);
  };

  const handleProductCreated = (newProduct: Product) => {
    setProducts([newProduct, ...products]);
    setShowAddModal(false);
  };

  const handleProductUpdated = (updatedProduct: Product) => {
    setProducts(products.map(p => 
      p.productId === updatedProduct.productId ? updatedProduct : p
    ));
    setShowEditModal(false);
    setSelectedProduct(null);
  };

  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (product: Product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"? This will mark it as inactive.`)) {
      return;
    }

    try {
      await productCatalogAPI.deleteProduct(product.productId);
      
      // Reload products to reflect changes
      loadData();
      
      window.alert('Product deleted successfully');
    } catch (err) {
      console.error('Error deleting product:', err);
      window.alert('Failed to delete product. Please try again.');
    }
  };

  // Selection handlers
  const handleSelectProduct = (productId: string) => {
    setSelectedProductIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedProductIds.size === filteredProducts.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(filteredProducts.map(p => p.productId)));
    }
  };

  const handleAddToPurchase = () => {
    const selected = filteredProducts.filter(p => selectedProductIds.has(p.productId));
    
    // Check which selected products are already on order
    const onOrderConflicts = selected
      .filter(p => productsOnOrder[p.productId])
      .map(p => ({
        product: p,
        orderInfo: productsOnOrder[p.productId]
      }));

    if (onOrderConflicts.length > 0) {
      // Show warning modal
      setProductsWithOpenOrders(onOrderConflicts);
      setShowDoubleOrderWarning(true);
    } else {
      // No conflicts, proceed directly
      navigateToPurchases(selected);
    }
  };

  const navigateToPurchases = (selectedProducts: Product[]) => {
    // Navigate to purchases page with selected products in state
    navigate('/purchases', {
      state: {
        productsToAdd: selectedProducts,
        fromProductCatalog: true
      }
    });
  };

  const handleDoubleOrderProceed = () => {
    const selected = filteredProducts.filter(p => selectedProductIds.has(p.productId));
    setShowDoubleOrderWarning(false);
    setProductsWithOpenOrders([]);
    navigateToPurchases(selected);
  };

  const handleDoubleOrderCancel = () => {
    setShowDoubleOrderWarning(false);
    setProductsWithOpenOrders([]);
  };

  // Get full category display (Parent > Subcategory or just Parent)
  const getCategoryDisplay = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    
    if (!category) {
      return categoryId; // Fallback to ID if not found
    }
    
    // If this category has a parent, show "Parent > Subcategory"
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      if (parent) {
        return `${parent.name} > ${category.name}`;
      }
    }
    
    // Otherwise just show the category name
    return category.name;
  };

  // Get unique category IDs from products for filter dropdown
  const uniqueCategoryIds = Array.from(new Set(products.map(p => p.categoryId)));
  const availableCategories = uniqueCategoryIds
    .map(id => ({ id, name: getCategoryDisplay(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Product Catalog</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, manufacturer, model..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {availableCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="SERIALIZED">Serialized</option>
              <option value="BULK">Bulk</option>
            </select>
          </div>

         {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Products</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Stock Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stock Level
            </label>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Stock Levels</option>
              <option value="low">⚠️ Low Stock (Needs Order)</option>
              <option value="on-order">🚚 On Order</option>
              <option value="out">🔴 Out of Stock</option>
            </select>
          </div>
        </div>
      </div>
      {/* Results Count & Selection Actions */}
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing {filteredProducts.length} of {products.length} products
        </div>
        {selectedProductIds.size > 0 && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-blue-600 font-medium">
              {selectedProductIds.size} selected
            </span>
            <button
              onClick={handleAddToPurchase}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Purchase
            </button>
            <button
              onClick={() => setSelectedProductIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading products...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Products Table */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredProducts.length > 0 && selectedProductIds.size === filteredProducts.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Typical Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No products found. Try adjusting your filters or add a new product.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const stock = productStock[product.productId];
                  const onOrder = productsOnOrder[product.productId];
                  const isLowStock = stock && stock.current < stock.min;
                  
                  return (
                    <tr key={product.productId} className={`hover:bg-gray-50 ${selectedProductIds.has(product.productId) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedProductIds.has(product.productId)}
                          onChange={() => handleSelectProduct(product.productId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        {product.model && (
                          <div className="text-sm text-gray-500">
                            Model: {product.model}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {getCategoryDisplay(product.categoryId)}
                      </td>
                      <td className="px-6 py-4">
                        {stock ? (
                          <div className="flex flex-col gap-1">
                            <div className={`text-sm font-medium ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                              {stock.current} / {stock.min}
                              {isLowStock && !onOrder && (
                                <AlertTriangle className="inline w-4 h-4 ml-1 text-amber-500" />
                              )}
                            </div>
                            {onOrder && (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                                <Truck className="w-3 h-3" />
                               {onOrder.totalPendingQuantity} on order
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                 <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.trackingType === 'SERIALIZED' 
                            ? 'bg-blue-100 text-blue-800' 
                            : product.trackingType === 'CONSUMABLE'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {product.trackingType || product.productType}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {product.typicalUnitCost 
                          ? `$${product.typicalUnitCost.toFixed(2)}` 
                          : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEditClick(product)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(product)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <AddProductModal
          onClose={() => setShowAddModal(false)}
          onProductCreated={handleProductCreated}
          categories={categories}
        />
      )}

     {/* Edit Product Modal */}
      {showEditModal && selectedProduct && (
        <EditProductModal
          product={selectedProduct}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          onProductUpdated={handleProductUpdated}
          categories={categories}
        />
      )}

      {/* Double Order Warning Modal */}
      <DoubleOrderWarningModal
        isOpen={showDoubleOrderWarning}
        onClose={handleDoubleOrderCancel}
        onProceed={handleDoubleOrderProceed}
        productsOnOrder={productsWithOpenOrders}
        totalSelectedCount={selectedProductIds.size}
      />
    </div>
  );
};

export default ProductCatalogPage;
